import axios from "axios";
import qs from "qs";

const API_BASE = "https://zhongsheyun.com/api";
// 与参考项目保持一致：miceId = ER8eYYCJk6BflK7nRKiXe
const MICE_ID = "ER8eYYCJk6BflK7nRKiXe";
// 腾讯云验证码 AppId
const CAPTCHA_APP_ID = "2049652090";

// 与参考项目 api.js 完全一致：表单格式提交
const instance = axios.create({
  baseURL: API_BASE,
  timeout: 15000,
  headers: {
    "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
  },
  // 自动将 POST body 用 qs 序列化为表单格式（与参考 qs.stringify 一致）
  // 数组字段用 indices 格式序列化（custom_xxx[0]=a&custom_xxx[1]=b），
  // 后端 PHP/Laravel 等可以正确解析为数组。repeat 格式（custom_xxx=a&custom_xxx=b）
  // 只有少数后端能按数组解析，多数只会取最后一个值。
  transformRequest: [(data) => qs.stringify(data, { arrayFormat: "indices" })],
});

// 统一抽取接口返回值：约定后端会把业务数据放在 data/根，code=0/200 或 success=true 表示成功
function unwrap(resp) {
  const body = resp && resp.data;
  if (!body) return { success: false, data: null, msg: "空响应" };
  // body 本身可能是业务对象（含 code/data/msg），也可能就是 data
  const hasBizEnvelope =
    typeof body.code !== "undefined" || typeof body.success !== "undefined";
  const biz = hasBizEnvelope ? body : { code: 0, success: true, data: body };
  const success = biz.code === 0 || biz.code === 200 || biz.success === true;
  return {
    success,
    data: biz.data != null ? biz.data : body,
    msg: biz.msg || biz.message || (success ? "" : "请求失败"),
    _raw: body,
  };
}

/**
 * 发送短信验证码（需要先完成腾讯云滑动验证，拿到 randstr + ticket）
 * POST /w/attendee/code
 * @param {object} payload
 * @param {string} payload.mobile
 * @param {string} payload.Randstr  腾讯云返回的 randstr
 * @param {string} payload.ticket   腾讯云返回的 ticket
 * @returns {Promise<{success:boolean, msg?:string, data?:any}>}
 */
export async function sendCaptchaCode({ mobile, Randstr, ticket }) {
  try {
    const resp = await instance.post("/w/attendee/code", {
      miceId: MICE_ID,
      mobile,
      Randstr,
      ticket,
    });
    return unwrap(resp);
  } catch (err) {
    const msg =
      (err.response &&
        err.response.data &&
        (err.response.data.msg || err.response.data.message)) ||
      "网络异常，请稍后重试";
    return { success: false, msg };
  }
}

/**
 * 提交报名前校验：输入手机号 + 短信验证码，换取 token
 * POST /w/attendee/verify
 * @param {object} payload
 * @param {string} payload.mobile
 * @param {string} payload.code   短信验证码（用户输入的 4~6 位）
 * @returns {Promise<{success:boolean, token?:string, msg?:string}>}
 */
export async function makeVerifyToken({ mobile, code }) {
  try {
    const resp = await instance.post("/w/attendee/verify", {
      miceId: MICE_ID,
      mobile,
      code,
    });
    const { success, data, msg, _raw } = unwrap(resp);
    // token 可能在 data.token，也可能根级返回
    const token = (_raw && _raw.token) || (data && data.token) || "";
    return { success: success && !!token, token, msg };
  } catch (err) {
    const msg =
      (err.response &&
        err.response.data &&
        (err.response.data.msg || err.response.data.message)) ||
      "网络异常，请稍后重试";
    return { success: false, msg };
  }
}

/**
 * 提交参观报名
 * POST /w/attendee
 * 传参契约严格对齐 visit-registration.vue 的 onSubmit：
 *   name / company / mobile / token / from_app / meetings
 *   并按历史要求追加 miceId（所有接口必须携带）
 * @param {object} payload
 * @param {string} payload.token            来自 makeVerifyToken 返回
 * @param {string} payload.company          单位名称
 * @param {string} payload.name             姓名
 * @param {string} payload.mobile           手机号
 * @param {string[]} payload.activities     参与活动（标题）列表，转为 meetings JSON
 * @param {string} [payload.from_app='website']
 * @returns {Promise<{success:boolean, msg?:string, data?:any}>}
 */
export async function submitAttendee({
  token,
  company,
  name,
  mobile,
  activities = "",
  from_app = "website",
}) {
  try {
    const resp = await instance.post("/w/attendee", {
      miceId: MICE_ID,
      token,
      name,
      "custom_868f44d1-f7e3-4759-bfd8-d87385f40060": company,
      mobile,
      from_app,
      "custom_2a8dc62a-7b4e-4157-9061-ee0dc98a8f7a": activities,
    });
    return unwrap(resp);
  } catch (err) {
    const msg =
      (err.response &&
        err.response.data &&
        (err.response.data.msg || err.response.data.message)) ||
      "网络异常，请稍后重试";
    return { success: false, msg };
  }
}

export { MICE_ID, CAPTCHA_APP_ID, API_BASE };

/**
 * 将后端返回的列表项 { article, category, tags } 归一化为扁平结构，
 * 并根据 publishTime（秒级时间戳）计算月份/日期用于卡片展示。
 */
function normalizeArticle(raw) {
  const a = (raw && raw.article) || raw || {};
  const ts = a.publishTime
    ? Number(a.publishTime) * 1000
    : a.createTime
    ? Number(a.createTime) * 1000
    : Date.now();
  const d = new Date(ts);
  const pad = (n) => String(n).padStart(2, "0");
  return {
    id: a.id,
    title: a.title || "",
    desc: a.subMessage || a.summary || "",
    cover: a.cover || "",
    htmlContent: a.html_content || a.content || "",
    publishTime: a.publishTime || "",
    createTime: a.createTime || "",
    jumpUrl: a.jump_url || "",
    categoryName: (raw && raw.category && raw.category.name) || "",
    month: `${d.getMonth() + 1}月`,
    day: pad(d.getDate()),
  };
}

/**
 * 展会资讯列表
 * GET /w/article/list
 * 与用户提供的契约一致：page=0 起始、pageSize、categoryId=12Jq1rdrZIACeiPXKmJNVu、by=category
 * @param {number} [page=0]
 * @param {number} [pageSize=10]
 * @returns {Promise<{success:boolean, list:any[], total:number, msg?:string, _raw:any}>}
 */
export async function getArticleList(page = 0, pageSize = 10) {
  try {
    // GET: params 由 axios 自动拼成 query string；miceId 按要求追加
    const resp = await instance.get("/w/article/list", {
      params: {
        page,
        pageSize,
        categoryId: "12Jq1rdrZIACeiPXKmJNVu",
        by: "category",
        miceId: MICE_ID,
      },
    });
    const { success, data, msg, _raw } = unwrap(resp);
    // data 可能 { list, count }，也可能直接是数组，两种都兼容
    let rawList = [];
    let total = 0;
    if (Array.isArray(data)) {
      rawList = data;
      total = data.length;
    } else if (data && typeof data === "object") {
      rawList = Array.isArray(data.list)
        ? data.list
        : Array.isArray(data.records)
        ? data.records
        : [];
      const c =
        typeof data.count === "number"
          ? data.count
          : typeof data.count === "string"
          ? parseInt(data.count, 10)
          : typeof data.total === "number"
          ? data.total
          : typeof data.totalCount === "number"
          ? data.totalCount
          : rawList.length;
      total = isNaN(c) ? rawList.length : c;
    }
    const list = rawList.map(normalizeArticle);
    return { success, list, total, msg, _raw };
  } catch (err) {
    const msg =
      (err.response &&
        err.response.data &&
        (err.response.data.msg || err.response.data.message)) ||
      "网络异常，请稍后重试";
    return { success: false, list: [], total: 0, msg };
  }
}

export async function getcustomizeinfo() {
  try {
    const resp = await instance.get("/w/mice/getcustomizeinfo", {
      params: {
        miceId: MICE_ID,
      },
    });
    return unwrap(resp);
  } catch (err) {
    const msg =
      (err.response &&
        err.response.data &&
        (err.response.data.msg || err.response.data.message)) ||
      "网络异常，请稍后重试";
    return { success: false, msg };
  }
}

/**
 * 展会资讯详情（通过 id/主键在 list 里找不到时使用；如果后端无详情接口，则回退为在列表里按 id 查找）
 * 如果接口提供 /w/article/detail 或类似路径，这里可替换实现。
 * 当前与 getArticleList 一致：用相同接口取全量后筛选，保障 id 型详情可访问。
 * @param {string|number} id
 * @returns {Promise<{success:boolean, article:any|null, msg?:string}>}
 */
export async function getArticleDetail(id) {
  try {
    // 先尝试按 id 取前 100 条，如果 id 命中即可
    const { success, list, msg } = await getArticleList(0, 100);
    if (!success) return { success: false, article: null, msg };
    const article = list.find((a) => String(a.id) === String(id)) || null;
    return {
      success: !!article,
      article,
      msg: article ? "" : msg || "未找到资讯详情",
    };
  } catch (err) {
    return {
      success: false,
      article: null,
      msg: "网络异常，请稍后重试",
    };
  }
}

export default instance;
